import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LockKey, CheckCircle, Circle, ArrowLeft } from "@phosphor-icons/react";

const RULES = [
  { key: "len", label: "Mindestens 10 Zeichen", test: (v) => v.length >= 10 },
  { key: "upper", label: "Ein Großbuchstabe", test: (v) => /[A-Z]/.test(v) },
  { key: "lower", label: "Ein Kleinbuchstabe", test: (v) => /[a-z]/.test(v) },
  { key: "digit", label: "Eine Ziffer", test: (v) => /\d/.test(v) },
];

export default function PasswortZuruecksetzen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const allOk = RULES.every((r) => r.test(pw));
  const match = pw && pw === pw2;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!allOk) { setError("Das Passwort erfüllt nicht alle Anforderungen."); return; }
    if (!match) { setError("Die Passwörter stimmen nicht überein."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Zurücksetzen fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md animate-fade-in" data-testid="reset-password-page">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <LockKey size={24} weight="bold" />
          </div>
          <div>
            <div className="font-heading text-lg font-black tracking-tight">Neues Passwort</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground">Zurücksetzen</div>
          </div>
        </div>

        {!token ? (
          <div className="space-y-4">
            <p data-testid="reset-password-error" className="text-sm font-medium text-danger">
              Ungültiger oder fehlender Reset-Link. Bitte fordern Sie einen neuen Link an.
            </p>
            <Link to="/passwort-vergessen" className="inline-flex items-center gap-2 text-sm font-medium hover:underline">
              <ArrowLeft size={16} /> Neuen Link anfordern
            </Link>
          </div>
        ) : done ? (
          <div className="space-y-4" data-testid="reset-password-success">
            <div className="flex items-center gap-2 text-emerald-600">
              <CheckCircle size={22} weight="fill" />
              <span className="font-semibold">Passwort zurückgesetzt</span>
            </div>
            <p className="text-sm text-muted-foreground">Sie können sich jetzt mit Ihrem neuen Passwort anmelden.</p>
            <Button data-testid="reset-password-to-login" onClick={() => navigate("/login")} className="h-12 w-full">
              Zur Anmeldung
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <p className="text-sm text-muted-foreground">Legen Sie Ihr neues Passwort fest.</p>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Neues Passwort</Label>
              <Input type="password" data-testid="reset-password-new" value={pw}
                onChange={(e) => setPw(e.target.value)} required className="h-12" autoComplete="new-password" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Passwort bestätigen</Label>
              <Input type="password" data-testid="reset-password-confirm" value={pw2}
                onChange={(e) => setPw2(e.target.value)} required className="h-12" autoComplete="new-password" />
            </div>
            <ul className="space-y-1.5 rounded-md border border-border bg-muted/40 p-4">
              {RULES.map((r) => {
                const ok = r.test(pw);
                return (
                  <li key={r.key} className={`flex items-center gap-2 text-sm ${ok ? "text-emerald-600" : "text-muted-foreground"}`}>
                    {ok ? <CheckCircle size={16} weight="fill" /> : <Circle size={16} />}{r.label}
                  </li>
                );
              })}
              <li className={`flex items-center gap-2 text-sm ${match ? "text-emerald-600" : "text-muted-foreground"}`}>
                {match ? <CheckCircle size={16} weight="fill" /> : <Circle size={16} />}Passwörter stimmen überein
              </li>
            </ul>
            {error && <p data-testid="reset-password-error" className="text-sm font-medium text-danger">{error}</p>}
            <Button type="submit" data-testid="reset-password-submit" disabled={loading || !allOk || !match}
              className="h-12 w-full text-base font-semibold">
              {loading ? "Speichern…" : "Passwort zurücksetzen"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
